import { tokenizer, tokTypes } from 'acorn'
import { CHAR_CAP, STRING_CHARS_PER_TOKEN, TOKEN_BUDGET } from './constants'

export type TokenError = { message: string; line: number; column: number }

export type TokenCount = {
  tokens: number
  chars: number
  overBudget: boolean
  overCap: boolean
  error?: TokenError
}

/** Closing brackets, separators and backquotes cost nothing. */
const FREE = new Set([
  tokTypes.parenR,
  tokTypes.bracketR,
  tokTypes.braceR,
  tokTypes.comma,
  tokTypes.semi,
  tokTypes.backQuote,
  tokTypes.eof,
])

/** Literals whose cost scales with their length. */
const textCost = (length: number, min: number): number =>
  Math.max(min, Math.ceil(length / STRING_CHARS_PER_TOKEN))

const tokenCost = (code: string, token: { type: unknown; start: number; end: number }): number => {
  if (FREE.has(token.type as never)) return 0
  if (token.type === tokTypes.string) return textCost(token.end - token.start - 2, 1)
  if (token.type === tokTypes.template) return textCost(token.end - token.start, 0)
  if (token.type === tokTypes.regexp) return textCost(token.end - token.start, 1)
  return 1
}

const toError = (err: unknown): TokenError => {
  const e = err as { message?: string; loc?: { line: number; column: number } }
  const message = (e.message ?? String(err)).replace(/\s*\(\d+:\d+\)$/, '')
  return { message, line: e.loc?.line ?? 1, column: e.loc?.column ?? 0 }
}

/**
 * Counts a plot's tokens PICO-8 style: names, keywords, numbers, operators and
 * opening brackets cost 1; strings cost 1 per 16 characters; whitespace,
 * comments, commas, semicolons and closing brackets are free.
 */
export const countTokens = (code: string): TokenCount => {
  let tokens = 0
  let error: TokenError | undefined
  try {
    for (const token of tokenizer(code, {
      ecmaVersion: 2022,
      sourceType: 'module',
      locations: true,
    })) {
      tokens += tokenCost(code, token)
    }
  } catch (err) {
    error = toError(err)
  }
  const chars = code.length
  const result: TokenCount = {
    tokens,
    chars,
    overBudget: tokens > TOKEN_BUDGET,
    overCap: chars > CHAR_CAP,
  }
  return error ? { ...result, error } : result
}
